import { useAppDispatch, useAppSelector } from '@/config/hooks';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MdTaskAlt } from 'react-icons/md';
import { useTranslation } from 'react-i18next';
import {
  Accordion,
  Avatar,
  Form,
  IconButton,
  InputNumber,
  List,
  Loader,
  Message,
  SelectPicker,
  Stack,
  TagPicker,
  useToaster
} from 'rsuite';
import PlusIcon from '@rsuite/icons/Plus';
import MinusIcon from '@rsuite/icons/Minus';
import {
  addIndividualAccess,
  getIndividualAccessByUser,
  updateIndividualAccess,
  updateUserAccessPriority
} from '@/services/access.service';
import { useParams } from 'react-router-dom';
import { getFloors } from '@/services/floor.service';
import { getZones } from '@/services/zone.service';
import { AppSelect, Icon, Trash } from '@rsuite/icons';
import { VscSave } from 'react-icons/vsc';
import { RiMapPinUserLine, RiUserLocationLine } from 'react-icons/ri';
import { getGroupAccesses } from '@/services/accessProfile.service';

const slimText = {
  fontSize: '0.9em',
  color: '#97969B',
  fontWeight: 'lighter',
  paddingBottom: 5,
  width: '200px'
};

const Header = props => {
  const { icon, title, name, ...rest } = props;
  return (
    <Stack {...rest} spacing={10} alignItems="flex-start">
      {icon}
      <Stack spacing={2} direction="column" alignItems="flex-start">
        <div>{title}</div>
        <div style={{ color: 'var(--rs-text-secondary)', fontSize: 12 }}>{name}</div>
      </Stack>
    </Stack>
  );
};
const AccessPriority = () => {
  const { id } = useParams();
  const dispatch = useAppDispatch();
  const toaster = useToaster();
  const { t } = useTranslation();

  const loading = useAppSelector((state: any) => state.access?.loading);
  const floors = useAppSelector((state: any) => state.floor?.data);

  const zones = useAppSelector((state: any) => state.zone?.data || [])
    ?.map(item => {
      return {
        ...item,
        value: item._id,
        label: `${item.floor?.building?.address} / ${item.floor?.name} / ${item?.name}`
      };
    })
    ?.sort((a, b) => a.floor.floor_num - b.floor.floor_num);

  const accessProfiles = useAppSelector((state: any) => state.accessProfile.data);

  // const [zones, setZones] = useState<any>([]);

  const accessData = useAppSelector((state: any) => state.access?.data);
  const accessProfile = useAppSelector((state: any) => state.users.item)?.Group?._id;
  const groupAccesses = useAppSelector((state: any) => state.accessProfile.groupAccess)?.sort(
    (a, b) => {
      const floorA = parseInt(a.zone.floor.floor_num, 10);
      const floorB = parseInt(b.zone.floor.floor_num, 10);
      return floorA - floorB;
    }
  );

  // useEffect(() => {
  //   if (zonesStore.length > 0 && floors.length > 0) {
  //     const result = zonesStore?.map(item => {
  //       return {
  //         ...item,
  //         value: item._id,
  //         label: item.floor.name?.name + ' / ' + item.name
  //       };
  //     });
  //     setZones(result?.sort((a, b) => b.floor.floor_num - a.floor.floor_num));
  //   }
  // }, [zonesStore, floors]);

  const groupZonesByPriority = array => {
    return Object.values(
      array.reduce((acc, { zone, dayOfWeek, priority }) => {
        const zoneId = zone?._id;
        if (!acc[zoneId]) {
          acc[zoneId] = { zone: zoneId, data: [] };
        }
        acc[zoneId].data.push({ dayOfWeek, priority });
        return acc;
      }, {})
    );
  };

  const access = useMemo(
    () => (accessData?.length > 0 ? groupZonesByPriority(accessData) : []),
    [accessData]
  );

  const daysOfWeek = [
    { label: t('Monday'), value: 1 },
    { label: t('Tuesday'), value: 2 },
    { label: t('Wednesday'), value: 3 },
    { label: t('Thursday'), value: 4 },
    { label: t('Friday'), value: 5 },
    { label: t('Saturday'), value: 6 },
    { label: t('Sunday'), value: 7 }
  ];

  const [localAccessList, setLocalAccessList] = useState(() =>
    accessData?.length > 0 ? groupZonesByPriority(accessData || []) : []
  );

  useEffect(() => {
    if (!localAccessList.length && access.length) {
      setLocalAccessList(access);
    }
  }, [access]);

  useEffect(() => {
    dispatch(getIndividualAccessByUser(id));
    dispatch(getFloors());
    dispatch(getZones());
  }, [id]);

  useEffect(() => {
    if (accessProfile) {
      dispatch(getGroupAccesses(accessProfile));
    }
  }, [accessProfile]);

  const handleAddZoneRow = () => {
    setLocalAccessList([
      ...localAccessList,
      {
        zone: null,
        data: [
          {
            priority: null,
            dayOfweek: null
          }
        ]
      }
    ]);
  };

  const handleAddRow = indexRow => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        return {
          ...row,
          data: [
            ...row.data,
            {
              dayOfWeek: null,
              priority: null
            }
          ]
        };
      })
    );
  };

  const handleSave = async () => {
    try {
      const flattenGroupedData = await localAccessList.flatMap(({ zone, data }) =>
        data.map(({ dayOfWeek, priority }) => ({ zone, dayOfWeek, priority }))
      );

      dispatch(await updateUserAccessPriority(id, flattenGroupedData))
        .then(() => {
          toaster.push(
            <Message type="success" duration={5000} showIcon>
              {t('Updated successfully')}
            </Message>,
            { placement: 'topCenter' }
          );
        })
        .catch(e => {
          toaster.push(
            <Message type="error" duration={5000} showIcon>
              {t(e.message)}
            </Message>,
            { placement: 'topCenter' }
          );
        });
    } catch (error) {
      console.error('Error in save :', error);
    }
  };

  // const handleChange = (indexRow: number, key: string, value: any, index) => {
  //   setLocalAccessList(prevList =>
  //     prevList.map((row, rowIndex) => {
  //       if (rowIndex !== indexRow) return row;
  //       if (index === null) {
  //         return { ...row, [key]: value };
  //       } else {
  //         const updatedData = row.data.map((item, itemIndex) =>
  //           itemIndex === index ? { ...item, [key]: value } : item
  //         );
  //         return { ...row, data: updatedData };
  //       }
  //     })
  //   );
  // };

  const handleChange = (indexRow: number, key: string, value: any, index: number | null = null) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;

        if (key === 'zone') {
          return {
            ...row,
            zone: value,
            data: daysOfWeek.map(day => ({
              dayOfWeek: day.value,
              priority: 1
            }))
          };
        }

        if (index === null) {
          return { ...row, [key]: value };
        } else {
          const updatedData = row.data.map((item, itemIndex) =>
            itemIndex === index ? { ...item, [key]: value } : item
          );
          return { ...row, data: updatedData };
        }
      })
    );
  };

  const handleDeleteZoneRow = indexRow => {
    setLocalAccessList(prevList => prevList.filter((_, rowIndex) => rowIndex !== indexRow));
  };

  const handleRemoveRow = (indexRow, index) => {
    setLocalAccessList(prevList =>
      prevList.map((row, rowIndex) => {
        if (rowIndex !== indexRow) return row;
        const updatedData = row.data.filter((_, itemIndex) => itemIndex !== index);
        return { ...row, data: updatedData };
      })
    );
  };

  return (
    <div>
      <Form fluid>
        <Accordion defaultActiveKey={1}>
          <Accordion.Panel
            header={
              <Header
                icon={<RiMapPinUserLine />}
                title={t('Profile accesses')}
                name={accessProfiles?.find(item => item._id == accessProfile)?.name}
              />
            }
            eventKey={1}
          >
            <List>
              {groupAccesses?.map((access, index) => (
                <List.Item key={index} style={{ fontSize: 12 }}>
                  <Stack justifyContent="space-between" wrap>
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>Zone</div>

                      <p style={{ color: 'var(--rs-text-primary-500)' }}>
                        <strong>{access.zone?.floor?.name}</strong> / {access.zone?.name}
                      </p>
                    </div>

                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Day of week')}</div>
                      <p>{daysOfWeek.find(item => item.value == access.dayOfWeek)?.label}</p>
                    </div>

                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Priority')}</div>
                      <p>{access.priority}</p>
                    </div>
                  </Stack>
                </List.Item>
              ))}
            </List>
          </Accordion.Panel>
        </Accordion>

        <Stack style={{ marginTop: 50, marginBottom: 10, marginLeft: 10 }} spacing={5}>
          <RiUserLocationLine /> <h6 className="title">{t('Manage individual user accesses')} </h6>
        </Stack>

        {loading ? (
          <Loader backdrop />
        ) : (
          <div className="ml-10">
            <Accordion>
              {localAccessList?.map((row, indexRow) => (
                <Accordion.Panel
                  key={indexRow}
                  header={
                    <Stack spacing={10} alignItems="flex-start">
                      <AppSelect style={{ color: 'var(--rs-text-primary)' }} />
                      {row.zone ? (
                        <p style={{ fontSize: 13, marginTop: 2 }}>
                          {
                            floors.find(
                              floor => floor._id == zones.find(z => z._id == row.zone)?.floor?._id
                            )?.building?.address
                          }{' '}
                          /{' '}
                          {
                            floors.find(
                              floor => floor._id == zones.find(z => z._id == row.zone)?.floor?._id
                            )?.name
                          }{' '}
                          / {zones.find(z => z._id == row.zone)?.name}
                        </p>
                      ) : (
                        t('No zone selected')
                      )}
                    </Stack>
                  }
                  defaultExpanded
                >
                  <Stack justifyContent="space-between">
                    <div style={{ textAlign: 'left' }}>
                      <div style={slimText}>{t('Zone')}</div>

                      <SelectPicker
                        data={zones}
                        onChange={value => handleChange(indexRow, 'zone', value, null)}
                        value={row.zone}
                        style={{ width: 250 }}
                        placement="auto"
                        disabledItemValues={
                          localAccessList?.length > 0
                            ? Array.from(localAccessList, ({ zone }) => zone)
                            : []
                        }
                        cleanable={false}
                      />
                    </div>
                    <IconButton
                      appearance="subtle"
                      size="sm"
                      icon={<Trash color="red" />}
                      onClick={() => {
                        console.log('delete');
                        handleDeleteZoneRow(indexRow);
                      }}
                      id={`delete-${row.zone}`}
                      title={t('Remove zone access')}
                    ></IconButton>
                  </Stack>
                  <List divider={false} style={{ marginLeft: 50 }}>
                    {row.data.map((item, index) => (
                      <List.Item key={index}>
                        <Stack spacing={20} wrap>
                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Day of week')}</div>

                            <SelectPicker
                              data={daysOfWeek}
                              onChange={value => handleChange(indexRow, 'dayOfWeek', value, index)}
                              value={item.dayOfWeek}
                              style={{ width: 250 }}
                              placement="auto"
                              disabledItemValues={
                                row.data?.length > 0
                                  ? Array.from(row.data, ({ dayOfWeek }) => dayOfWeek)
                                  : []
                              }
                            />
                          </div>

                          <div style={{ textAlign: 'left' }}>
                            <div style={slimText}>{t('Priority')}</div>

                            <InputNumber
                              onChange={value => handleChange(indexRow, 'priority', value, index)}
                              value={item.priority}
                              style={{ width: 100 }}
                            />
                          </div>
                          <Stack spacing={5}>
                            <IconButton
                              onClick={() => {
                                handleRemoveRow(indexRow, index);
                              }}
                              icon={<MinusIcon />}
                              size="xs"
                            />
                            {index == row.data?.length - 1 && (
                              <IconButton
                                onClick={() => {
                                  handleAddRow(indexRow);
                                }}
                                icon={<PlusIcon />}
                                size="xs"
                                id="add-access"
                              />
                            )}
                          </Stack>
                        </Stack>
                      </List.Item>
                    ))}
                  </List>
                </Accordion.Panel>
              ))}
            </Accordion>
            <IconButton
              onClick={handleAddZoneRow}
              icon={<PlusIcon color="var(--rs-primary-800)" />}
              style={{ marginTop: 10 }}
            />
          </div>
        )}
      </Form>

      <IconButton
        appearance="primary"
        icon={<Icon as={VscSave} />}
        style={{ float: 'right' }}
        onClick={handleSave}
      >
        {t('Save')}
      </IconButton>
    </div>
  );
};

export default AccessPriority;
